/**
 * Build a canonical subset using the currently selected/visible work items.
 *
 * The legacy Process Advisor filter engine remains the filter authority during
 * Sprint 1.  This adapter projects those selected work items onto the canonical
 * model, allowing Evidence/summary metrics to be calculated canonically without
 * re-implementing every filter type at once.
 */
export function projectCanonicalDataset(dataset, selection = {}) {
  if (!dataset) return null;

  const ids = new Set((selection.caseIds || []).map(v => String(v || '')).filter(Boolean));
  const keys = new Set((selection.caseKeys || []).map(v => String(v || '')).filter(Boolean));
  const includeAll = selection.includeAll === true || (!ids.size && !keys.size && selection.emptyMeansAll === true);

  const cases = (dataset.cases || []).filter(c => {
    if (includeAll) return true;
    const id = String(c?.id || '');
    const key = String(c?.key || c?.source?.nativeKey || '');
    const nativeId = String(c?.source?.nativeId || '');
    return ids.has(id) || ids.has(nativeId) || keys.has(key);
  });

  const selectedIds = new Set();
  const selectedKeys = new Set();
  for (const c of cases) {
    if (c?.id != null) selectedIds.add(String(c.id));
    if (c?.source?.nativeId != null) selectedIds.add(String(c.source.nativeId));
    if (c?.key != null) selectedKeys.add(String(c.key));
    if (c?.source?.nativeKey != null) selectedKeys.add(String(c.source.nativeKey));
  }

  const events = (dataset.events || []).filter(e => {
    const caseId = String(e?.caseId || '');
    const caseKey = String(e?.caseKey || e?.task || '');
    return selectedIds.has(caseId) || selectedKeys.has(caseKey);
  });

  return {
    ...dataset,
    cases,
    events,
    metadata: {
      ...(dataset.metadata || {}),
      projection: {
        type: 'legacy-filter-selection',
        selectedCaseCount: cases.length,
        selectedEventCount: events.length,
        sourceCaseCount: (dataset.cases || []).length,
        sourceEventCount: (dataset.events || []).length,
        ...(selection.metadata || {}),
      },
    },
  };
}

/**
 * Convert Process Advisor's current legacy task filter state into a canonical
 * selection.  Only task keys/native ids are consumed; internal numeric ids are
 * deliberately ignored because they are miner-local identifiers.
 */
export function canonicalSelectionFromLegacyTasks(tasks = []) {
  const active = (Array.isArray(tasks) ? tasks : []).filter(t => Number(t?.filtered || 0) === 0);
  return {
    caseKeys: active.map(t => t?.k ?? t?.task ?? t?.key).filter(v => v !== undefined && v !== null && String(v) !== ''),
    caseIds: active.map(t => t?.task_id ?? t?.native_id ?? t?.sys_id ?? t?.issue_id).filter(v => v !== undefined && v !== null && String(v) !== ''),
    metadata: { activeLegacyTaskCount: active.length },
  };
}
